// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Single Note Play
//
// Play a note (shown as a letter name) on the piano.
import type { NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import { isNoteName, noteLetterIntegers } from 'shared/definitions/musicTheory/notesAndAccidentals';
import { defaultsDeep } from 'shared/src/utils/defaultsDeep';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';

import * as Skills from '../uTheory.Skills/skills';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import type { InteractiveRegistryEntry } from '../uTheory.Skills/interactiveRegistry';
import Note from '../uTheory.Notation/note';

export interface SingleNotePlayOptions extends InteractiveOptions {
  lowestNote?: NoteName;
  highestNote?: NoteName;
  ignoreOctave?: boolean;
  questions?: NoteName[];
}

interface SingleNotePlayQuestion {
  skills: (string | number)[];
  text: string[];
  note: Note;
}

export default class SingleNotePlay extends InteractivePrototype {
  declare options: SingleNotePlayOptions;

  declare currentQuestion: SingleNotePlayQuestion;

  declare questions: SingleNotePlayQuestion[];

  constructor(displayDiv: HTMLDivElement, options: SingleNotePlayOptions) {
    super(displayDiv, options);
    this.targetSpeedFromSkills = SingleNotePlay.registry.skills;
    this.settings = {
      instructions: {
        ariaLive: 'alert',
        ariaLiveRegion: 0,
      },
      piano: {
        highlightPressed: false,
        ariaNoteDescriptionType: 'location',
        ariaLive: 'off',
        lowestNote: options.lowestNote,
        highestNote: options.highestNote,
      },
      checkMark: {
        rightAnswerLength: 0.4,
        rightAnswerInOutRatio: 0.2,
        wrongAnswerLength: 1.0,
        wrongAnswerInOutRatio: 0.3,
      },
      progressTracker: true,
      player: {},
    };

    this.layout = {
      instructions: {
        noWrap: true,
      },
    };

    this.targetSpeedFromSkills = [
      'piano_c_g',
      'piano_a_b',
      'sharps',
      'flats',
      'double_sharps',
      'double_flats',
    ];

    this.init(displayDiv, options);
  }

  init(displayDiv: HTMLDivElement, options: SingleNotePlayOptions) {
    // Lowest & highest notes must be set for non-ignore-octave
    if (
      !options.ignoreOctave &&
      (typeof options.lowestNote === 'undefined' || typeof options.highestNote === 'undefined')
    ) {
      throw Error(
        'ignoreOctave cannot be set to false in singleNotePlay unless lowestNote and highestNote are defined'
      );
    }

    // Skills must be set if we're in drill mode
    if (options.drill && !options.skills?.length) {
      throw Error('Skills must be provided in drill mode or singleNotePlay');
    }

    // If manual mode, but no questions, error:
    if (!options.drill && !options.testMode && !options.questions?.length) {
      throw Error('Questions must be provided, or drill set to true, for singleNotePlay.');
    }

    options = defaultsDeep(options, {
      lowestNote: 'C4',
      highestNote: 'C5',
      ignoreOctave: true,
      maxQuestions: 15,
    });

    // now call the prototype's init function:
    return super.init(displayDiv, options);
  }

  displayInstructions() {
    super.displayInstructions();
    if (!this.options.ignoreOctave) {
      this.piano!.labelNote('C4', 'C4', false, { class: '', 'font-family': '' });
    }
  }

  generateQuestions(questions = this.options.questions) {
    if (!this.options.drill) {
      this.questions = questions!.map((q, i) => this.processQuestion(q, i));
    } else {
      this.questions = this.randomQuestionGenerator().map((q, i) => this.processQuestion(q, i));
    }

    this.questionPrompt =
      typeof this.options.questionPrompt !== 'undefined'
        ? this.options.questionPrompt
        : 'Play the note {question}.';

    if (this.options.drill) {
      this.questionPrompt += ` Number {question} of ${this.questions.length}`;
    }
  }

  processQuestion(questionString: string, questionNumber: number): SingleNotePlayQuestion {
    if (!isNoteName(questionString)) {
      throw Error(`Question must be a note name in singleNotePlay. Got: ${questionString}`);
    }
    const note = new Note(questionString);
    let noteString = note.toAccidentalFontShortString();
    if (!this.options.ignoreOctave) noteString += note.octave;

    return {
      note: new Note(questionString),
      text: [noteString, `${questionNumber + 1}`],
      skills: [this.calculateSkill(note)],
    };
  }

  randomQuestionGenerator() {
    const skills = this.options.skills!;
    const skillObjects: { skill: string; probability: number }[] = [];
    skills.forEach((skill) => {
      const skillString = Skills.makeString(skill);
      let probability;
      switch (skillString) {
        case 'piano_c_g':
          probability = 5;
          break;
        case 'piano_a_b':
          probability = 2;
          break;
        case 'double_sharps':
          probability = 3;
          break;
        case 'double_flats':
          probability = 3;
          break;
        default:
          probability = 7;
          break;
      }
      skillObjects.push({ skill: skillString, probability });
    }, this);

    const generator = new QuestionGenerator();
    return generator.multipleSkillsGenerate(skillObjects, this.options.maxQuestions, this.options);
  }

  addListeners() {
    this.piano!.addNoteOnListener(this.check.bind(this));
  }

  calculateSkill(note: Note) {
    const noteInt = noteLetterIntegers[note.letter];
    switch (note.accidental) {
      case '#':
        return 'sharps';
      case 'b':
        return 'flats';
      case 'x':
        return 'double_sharps';
      case 'bb':
        return 'double_flats';
      case '':
        return noteInt >= 5 ? 'piano_a_b' : 'piano_c_g';
    }
    return -1;
  }

  private isCorrect(expectedNote: Note, givenMidi?: number) {
    if (typeof givenMidi !== 'number') return false;
    const studentAnswerNote = Note.fromMidi(givenMidi);
    const isCorrect = this.options.ignoreOctave
      ? studentAnswerNote.equalsEnharmonicallyIgnoreOctave(this.currentQuestion.note)
      : studentAnswerNote.equalsEnharmonically(this.currentQuestion.note);
    return isCorrect;
  }

  check(midiNumber: number) {
    console.log({ midiNumber });
    if (this.optionsTrue('testMode')) {
      this.options.ignoreOctave = true;
      this.checkpointAnswer(midiNumber);
      return;
    }
    if (this.rightAnswerDelay) return;

    const isCorrect = this.isCorrect(this.currentQuestion.note, midiNumber);
    if (isCorrect) {
      this.piano!.colorNote(midiNumber);
      window.setTimeout(() => {
        this.piano!.clearNoteColoring(midiNumber);
      }, 750);
      this.player!.play(midiNumber, { startTime: 0, stopTime: 0.9 });
      this.rightAnswer(750, { noPlay: true });
    } else this.wrongAnswer();
  }

  // ## CheckPoint Mode Questions:
  newCheckpointQuestion(questionString: NoteName) {
    return this.loadingPromise.then(() => {
      this.generateQuestions([questionString]);
      this.newQuestion();
      this.resize();
      return Promise.resolve(this.currentQuestion);
    });
  }

  getAnswerAndData(midiNumber?: number) {
    const unanswered = typeof midiNumber === 'undefined';
    const correct = this.isCorrect(this.currentQuestion.note, midiNumber);

    if (correct) {
      this.piano!.colorNote(midiNumber!);
    } else if (!unanswered) {
      this.piano!.colorNote(midiNumber, 'red');
    }
    const SVG = this.piano!.exportSVG();
    if (!unanswered) this.piano!.clearNoteColoring(midiNumber);

    const questionString = this.options.ignoreOctave
      ? this.currentQuestion.note.toShortString()
      : this.currentQuestion.note.toString();

    let answerString: NoteName | '' = questionString;
    if (!correct) {
      if (unanswered) answerString = '';
      else {
        const answerNote = Note.fromMidi(midiNumber);
        answerString = this.options.ignoreOctave
          ? answerNote.toShortString()
          : answerNote.toString();
      }
    }

    const answerAndData = {
      question: this.options.ignoreOctave
        ? this.currentQuestion.note.toShortString()
        : this.currentQuestion.note.toString(),
      questionType: 'SingleNotePlay',
      questionText: this.instructionsDiv!.innerHTML,
      studentAnswerSVG: SVG,
      studentAnswerText: answerString,
      correctAnswerText: questionString,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: this.currentQuestion.skills,
    };

    return answerAndData;
  }

  gameOver() {
    this.instructionsDiv!.innerHTML = 'Nice work!';
    this.instructionsDiv!.setAttribute('role', 'alert');
    this.piano!.removeListeners();
    super.gameOver();
  }

  static registry: InteractiveRegistryEntry = {
    description: 'Play notes on the piano',
    skills: ['piano_c_g', 'piano_a_b', 'sharps', 'flats', 'double_sharps', 'double_flats'],
    options: {
      lowestNote: 'G3',
      highestNote: 'C5',
      ignoreOctave: true,
    },
  };
}
